// EventsRegistration.js
import { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import FloatingLabelInput from "../Generic/FloatingLabelInput";
import LoadingSpinner from "../Generic/LoadingSpinner";
import { create } from "../../api/API";
import { getCurrentUser, isLoggedIn } from "../../utility/UserUtils";

const EventsRegistration = ({ show, onHide, event, onRegistered }) => {
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [user, setUser] = useState(null);

  // Optimistic UI state
  const [registered, setRegistered] = useState(false);
  const isPaidEvent = (event?.price ?? 0) > 0;
  // Load current user when modal opens
  useEffect(() => {
    if (isLoggedIn()) {
      setUser(getCurrentUser());
    }
    // reset optimistic state every time modal opens
    setRegistered(false);
  }, [show]);

  //  Check if already registered
  const isAlreadyRegistered =
    registered ||
    event?.attendees?.some((att) => {
      const memberId = att.member?.id || att.member?._id || att.member;
      return memberId?.toString() === user?.id?.toString();
    });

  const isEventFull = () => {
    if (!event?.capacity) return false;
    return (event.attendeesCount ?? 0) >= event.capacity;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      toast.info("Please login to register");
      window.location.href = "/login";
      return;
    }

    if (isAlreadyRegistered) {
      toast.info("You are already registered for this event");
      return;
    }

    if (isEventFull()) {
      toast.error("Event is full");
      return;
    }

    if (isPaidEvent) {
      await openRazorpay();
    } else {
      await registerFreeEvent();
    }
  };

  //  Free event registration
  const registerFreeEvent = async () => {
    setLoading(true);
    try {
      await create("/events-register-free", {
        eventId: event.id,
      });

      toast.success("Registered successfully!");
      setRegistered(true);
      onRegistered?.({
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
      });
      onHide();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  //paid event razorpay
  const openRazorpay = async () => {
    setLoading(true);

    try {
      const order = await create("/payments-create-order", {
        paymentFor: "event",
        referenceId: event.id,
      });

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY,
        amount: order.amount, // paise
        currency: "INR",
        name: "WISE Events",
        description: `Registration for ${event.name}`,
        order_id: order.id,

        handler: async (response) => {
          try {
            setVerifying(true);

            await create("/payments-verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            toast.success("Payment successful & registered!");
            setRegistered(true);
            onRegistered?.({
              id: user.id,
              firstName: user.firstName,
              lastName: user.lastName,
              email: user.email,
            });
            onHide();
          } catch (err) {
            console.error(err);
            toast.error("Payment succeeded but verification failed");
          } finally {
            setVerifying(false);
          }
        },

        prefill: {
          name: `${user.firstName || ""} ${user.lastName || ""}`,
          email: user.email,
        },

        theme: { color: "#e0b0ff" },
      };

      const rzp = new window.Razorpay(options);

      rzp.on("payment.failed", () => {
        toast.error("Payment failed. Please try again.");
      });

      rzp.open();
    } catch (err) {
      console.error(err);
      toast.error("Unable to initiate payment");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={verifying ? () => {} : onHide}
        backdrop={verifying ? "static" : true}
        keyboard={!verifying}
        centered
      >
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>
            {isPaidEvent ? "Pay & Register" : "Register for Event"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <form onSubmit={handleSubmit}>
            <FloatingLabelInput
              label="Name"
              value={
                user?.firstName ? `${user.firstName} ${user.lastName}` : ""
              }
              readOnly
            />

            <FloatingLabelInput
              label="Email"
              value={user?.email || ""}
              readOnly
            />

            <FloatingLabelInput
              label="Cohort"
              value={
                typeof user?.cohort === "object"
                  ? user.cohort?.title
                  : user?.cohort || "N/A"
              }
              readOnly
            />

            {event.price > 0 && <p className="mt-3">💰 Fee: ₹{event.price}</p>}

            {event.capacity && (
              <p className="mt-2">
                Capacity: {event.attendeesCount ?? 0}/{event.capacity}
              </p>
            )}

            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button variant="secondary" onClick={onHide} disabled={verifying}>
                Cancel
              </Button>

              <Button
                type="submit"
                variant="primary"
                disabled={
                  loading || verifying || isAlreadyRegistered || isEventFull()
                }
              >
                {isAlreadyRegistered
                  ? "Already Registered"
                  : isEventFull()
                    ? "Event Full"
                    : event.price > 0
                      ? "Pay & Register"
                      : "Register"}
              </Button>
            </div>
          </form>
        </Modal.Body>

        {(loading || verifying) && (
          <div className="loading-overlay">
            <LoadingSpinner
              text={verifying ? "Verifying payment..." : "Processing..."}
            />
          </div>
        )}
      </Modal>
    </>
  );
};

export default EventsRegistration;
